import { forwardRef, useImperativeHandle } from 'react'
import type { Agent } from '../../services/agentService'

// Controller & Layout
import { useChatPanelController } from './hooks/controller/useChatPanelController'
import { PanelLayout } from './components/Layout/PanelLayout'
import { ChatPanelErrorBoundary } from './components/ChatPanelErrorBoundary'

// Context
import { ChatProvider } from '../../contexts/ChatContext'

// Views
import { ChatView } from './components/Views/ChatView'
import { SettingsView } from './components/Views/SettingsView'
import { HistoryView } from './components/Views/HistoryView'
import { AgentSearchView } from './components/Views/AgentSearchView'
import { OutboxWorker } from './components/OutboxWorker'

export interface ChatPanelRef {
    startNewConversation: () => void
    setShowHistory: (show: boolean) => void
    setShowSettings: (show: boolean) => void
}

interface ChatPanelMasterProps {
    isOpen: boolean
    onClose: () => void
    onWidthChange?: (width: number) => void
    onFullscreenChange?: (isFullscreen: boolean) => void
    isFullscreen?: boolean
    agentId?: string | null
    agentName?: string
    agent?: Agent | null
    isEmbedded?: boolean
    position?: 'left' | 'right'
    startEmpty?: boolean
}

export const ChatPanelMaster = forwardRef<ChatPanelRef, ChatPanelMasterProps>(({
    isOpen,
    onClose,
    onFullscreenChange,
    isFullscreen = false,
    agentId: initialAgentId = null,
    agentName: initialAgentName = '',
}, ref) => {
    // ===== CONTROLLER (Single Source of Truth) =====
    const controller = useChatPanelController({
        isOpen,
        onClose,
        onFullscreenChange,
        isFullscreen,
        agentId: initialAgentId,
        agentName: initialAgentName,
    })

    const { state, actions, tabManager, dragHandlers, refs } = controller

    // ===== IMPERATIVE HANDLE (Legacy API) =====
    useImperativeHandle(ref, () => ({
        startNewConversation: actions.startNewChat,
        setShowHistory: (show: boolean) => actions.setView(show ? 'history' : 'chat'),
        setShowSettings: (show: boolean) => actions.setView(show ? 'settings' : 'chat'),
    }), [actions])

    // ===== EARLY RETURN =====
    if (!isOpen) return null

    // ===== RENDER =====
    return (
        <ChatProvider
            agentId={state.currentAgentId}
            agentName={state.currentAgentName}
        >
            <OutboxWorker />
            <ChatPanelErrorBoundary
                currentAgentId={state.currentAgentId}
                currentView={state.view}
            >
                <PanelLayout
                    state={state}
                    actions={actions}
                    tabManager={tabManager ? { tabs: tabManager.tabs, activeTabId: tabManager.activeTabId } : undefined}
                    dragHandlers={dragHandlers}
                    refs={refs}
                >
                    {/* View Routing - Industrial Isolation */}
                    {state.view === 'search' ? (
                        <AgentSearchView
                            onSelectAgent={(id, name) => {
                                if (tabManager) {
                                    tabManager.openTab(id, name)
                                }
                                actions.setView('chat')
                            }}
                            isFullscreen={isFullscreen}
                        />
                    ) : state.view === 'history' ? (
                        <HistoryView
                            onClose={() => actions.setView('chat')}
                        />
                    ) : state.view === 'settings' ? (
                        <SettingsView
                            settingsTab="llm" // Minimal shell state
                            setSettingsTab={() => { }} // Non-functional in shell
                            setShowSettings={(show) => actions.setView(show ? 'settings' : 'chat')}
                            isFullscreen={isFullscreen}
                        />
                    ) : (
                        <ChatView
                            isFullscreen={isFullscreen}
                        />
                    )}
                </PanelLayout>
            </ChatPanelErrorBoundary>
        </ChatProvider>
    )
})
